import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, Wrench } from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { useEquipmentSummary } from '@/hooks/useEquipmentRentals';

/**
 * EquipmentHomeCard — compact dashboard surface that mirrors the
 * CylinderHomeCard shape. Reads orders.v_equipment_summary (one row per
 * customer with active asset count + monthly rent total). Clicking the
 * card routes to /account where the full equipment list lives.
 *
 * Self-hides when the customer has no equipment on rent.
 */
export function EquipmentHomeCard() {
  const navigate = useNavigate();
  const { data: summary, isLoading } = useEquipmentSummary();
  const count = summary?.assetCount ?? 0;
  const monthly = summary?.monthlyRentTotal ?? 0;

  if (!isLoading && count === 0) return null;

  return (
    <button
      type="button"
      onClick={() => navigate('/account#equipment')}
      className={cn(
        'group block w-full text-left rounded-2xl border-2 border-brix-primary-deep bg-brix-surface overflow-hidden shadow-card-deep cursor-pointer transition-transform hover:-translate-y-0.5',
      )}
    >
      <div className="h-3 bg-brix-primary-deep" aria-hidden />
      <div className="px-6 md:px-8 pt-5 pb-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brix-primary-tint flex items-center justify-center text-brix-primary-deep shrink-0">
              <Wrench size={18} />
            </div>
            <h2 className="font-display text-xl font-bold tracking-tight text-slate-900 leading-none">
              Equipment on rent
            </h2>
          </div>
          <ArrowRight
            size={16}
            className="text-brix-primary transition-transform group-hover:translate-x-0.5"
          />
        </div>
        {isLoading ? (
          <div className="text-sm text-slate-900 tracking-tight inline-flex items-center gap-2 mt-3">
            <Loader2 size={13} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <div className="text-[10px] tracking-tight text-slate-900 font-bold">
                Active units
              </div>
              <div
                className="font-mono text-3xl font-bold text-slate-900 tracking-tight leading-none mt-1"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {count}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-tight text-slate-900 font-bold">
                Monthly rent
              </div>
              <div
                className="font-mono text-3xl font-bold text-brix-primary tracking-tight leading-none mt-1"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatUsd(monthly)}
              </div>
            </div>
          </div>
        )}
        <div className="text-[11px] text-brix-primary font-semibold tracking-tight inline-flex items-center gap-1 mt-3">
          See full list <ArrowRight size={11} />
        </div>
      </div>
    </button>
  );
}
